import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDepartmentDisplayName, formatSalary } from '../utils/jobUtils';

const SimilarJobs = ({ currentJobId, department, jobType, location }) => {
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  useEffect(() => {
    if (currentJobId && department) {
      fetchSimilarJobs();
    }
  }, [currentJobId, department, jobType, location]);

  const fetchSimilarJobs = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🔍 Tìm việc làm tương tự cho phòng ban: ${department}`);

      // Fetch jobs with similar criteria - ưu tiên phòng ban
      let response = await fetch(`${API_BASE_URL}/api/jobs/department/${department}?limit=10`);
      
      // Fallback to general jobs if department endpoint fails
      if (!response.ok) {
        console.log('⚠️ Department endpoint failed, falling back to general jobs');
        response = await fetch(`${API_BASE_URL}/api/jobs?limit=20`);
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch similar jobs');
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        // Filter similar jobs based on criteria với ưu tiên phòng ban
        const filtered = data.data
          .filter(job => job.id !== currentJobId) // Exclude current job
          .filter(job => job.status === 'active' || !job.status) // Only active jobs
          .map(job => ({
            ...job,
            similarity: calculateSimilarity(job)
          }))
          .sort((a, b) => b.similarity - a.similarity) // Sort by similarity
          .slice(0, 4); // Take top 4 similar jobs

        console.log(`✅ Tìm thấy ${filtered.length} việc làm tương tự`);
        setSimilarJobs(filtered);
      } else {
        setSimilarJobs(getFallbackSimilarJobs());
      }
    } catch (err) {
      console.error('Error fetching similar jobs:', err);
      setError(err.message);
      setSimilarJobs(getFallbackSimilarJobs());
    } finally {
      setLoading(false);
    }
  };

  // Calculate similarity score - Ưu tiên phòng ban
  const calculateSimilarity = (job) => {
    let score = 0;
    
    // Same department gets HIGHEST priority (10 points)
    if (job.department === department) score += 10;
    
    // Related departments get medium score
    const relatedDepartments = getRelatedDepartments(department);
    if (relatedDepartments.includes(job.department)) score += 6;
    
    // Same job type gets medium score (3 points)
    if (job.job_type === jobType) score += 3;
    
    // Same location gets low score (1 point)
    if (job.location === location) score += 1;
    
    // Featured jobs get slight boost (0.5 points)
    if (job.featured) score += 0.5;
    
    // Recent jobs get slight boost
    const createdDate = new Date(job.created_at || job.createdAt);
    const daysSinceCreated = (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceCreated < 30) score += 0.3; // Jobs posted within 30 days
    
    return score;
  };

  // Get related departments for better matching
  const getRelatedDepartments = (dept) => {
    const departmentRelations = {
      'engineering': ['product', 'design', 'operations'],
      'design': ['engineering', 'product', 'marketing'],
      'marketing': ['sales', 'design', 'product'],
      'sales': ['marketing', 'business', 'operations'],
      'hr': ['operations', 'finance'],
      'finance': ['hr', 'operations', 'business'],
      'operations': ['engineering', 'hr', 'finance'],
      'product': ['engineering', 'design', 'marketing']
    };
    
    return departmentRelations[dept] || [];
  };

  // Fallback data - Dynamic based on department
  const getFallbackSimilarJobs = () => {
    const departmentJobs = {
      'engineering': [
        {
          id: 99,
          title: 'Senior Frontend Developer',
          slug: 'senior-frontend-developer',
          department: 'engineering',
          job_type: jobType || 'full-time',
          location: location || 'TP.HCM',
          min_salary: 18000000,
          max_salary: 30000000,
          featured: true,
          remote_allowed: true,
          similarity: 10
        },
        {
          id: 98,
          title: 'Backend Engineer',
          slug: 'backend-engineer',
          department: 'engineering',
          job_type: 'full-time',
          location: location || 'TP.HCM',
          min_salary: 20000000,
          max_salary: 35000000,
          featured: false,
          remote_allowed: true,
          similarity: 9.5
        },
        {
          id: 97,
          title: 'UI/UX Designer',
          slug: 'ui-ux-designer',
          department: 'design',
          job_type: jobType || 'full-time',
          location: location || 'TP.HCM',
          min_salary: 15000000,
          max_salary: 25000000,
          featured: false,
          remote_allowed: false,
          similarity: 6.5
        }
      ],
      'marketing': [
        {
          id: 96,
          title: 'Digital Marketing Specialist',
          slug: 'digital-marketing-specialist',
          department: 'marketing',
          job_type: jobType || 'full-time',
          location: location || 'TP.HCM',
          min_salary: 12000000,
          max_salary: 20000000,
          featured: true,
          remote_allowed: true,
          similarity: 10
        },
        {
          id: 95,
          title: 'Content Creator',
          slug: 'content-creator',
          department: 'marketing',
          job_type: 'full-time',
          location: location || 'TP.HCM',
          min_salary: 10000000,
          max_salary: 18000000,
          featured: false,
          remote_allowed: true,
          similarity: 9
        },
        {
          id: 94,
          title: 'Sales Executive',
          slug: 'sales-executive',
          department: 'sales',
          job_type: jobType || 'full-time',
          location: location || 'TP.HCM',
          min_salary: 8000000,
          max_salary: 15000000,
          featured: false,
          remote_allowed: false,
          similarity: 6
        }
      ],
      'sales': [
        {
          id: 93,
          title: 'Sales Manager',
          slug: 'sales-manager',
          department: 'sales',
          job_type: jobType || 'full-time',
          location: location || 'TP.HCM',
          min_salary: 20000000,
          max_salary: 35000000,
          featured: true,
          remote_allowed: false,
          similarity: 10
        },
        {
          id: 92,
          title: 'Business Development',
          slug: 'business-development',
          department: 'sales',
          job_type: 'full-time',
          location: location || 'TP.HCM',
          min_salary: 15000000,
          max_salary: 25000000,
          featured: false,
          remote_allowed: true,
          similarity: 9
        }
      ]
    };

    // Get jobs for current department first, then add related
    const currentDeptJobs = departmentJobs[department] || [];
    const relatedDepartments = getRelatedDepartments(department);
    
    let allJobs = [...currentDeptJobs];
    
    // Add jobs from related departments
    relatedDepartments.forEach(dept => {
      if (departmentJobs[dept]) {
        allJobs = allJobs.concat(departmentJobs[dept].slice(0, 1)); // Add 1 job from each related dept
      }
    });

    // Sort by similarity and take top 4
    return allJobs
      .sort((a, b) => b.similarity - a.similarity)
      .slice(0, 4);
  };

  if (loading) {
    return (
      <div className="similar-jobs">
        <h3>🔍 Việc làm tương tự</h3>
        <div className="similar-jobs-loading">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="similar-job-skeleton">
              <div className="skeleton-line"></div>
              <div className="skeleton-line short"></div>
              <div className="skeleton-line short"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="similar-jobs">
      <h3>🔍 Việc làm tương tự</h3>
      <p className="similar-jobs-note">
        Ưu tiên hiển thị việc làm cùng phòng ban: <strong>{getDepartmentDisplayName(department)}</strong>
      </p>
      <div className="similar-jobs-list">
        {similarJobs.length > 0 ? (
          similarJobs.map(job => (
            <Link 
              key={job.id} 
              to={`/jobs/${job.slug || job.id}`} 
              className={`similar-job-item ${job.department === department ? 'same-department' : ''}`}
            >
              <div className="similar-job-header">
                <h4 className="similar-job-title">{job.title}</h4>
                <div className="similar-job-badges">
                  {job.department === department && <span className="same-dept-badge">🎯 Cùng phòng ban</span>}
                  {job.featured && <span className="featured-mini">⭐</span>}
                  {job.remote_allowed && <span className="remote-mini">🌐</span>}
                </div>
              </div>
              
              <div className="similar-job-meta">
                <span className={`job-department ${job.department === department ? 'highlight-dept' : ''}`}>
                  🏢 {getDepartmentDisplayName(job.department)}
                </span>
                <span className="job-location">📍 {job.location}</span>
              </div>
              
              <div className="similar-job-salary">
                💰 {formatSalary(job.min_salary, job.max_salary)}
              </div>
              
              {/* Similarity indicator */}
              <div className="similarity-indicator">
                <div className="similarity-score">
                  Độ phù hợp: {Math.round((job.similarity / 10) * 100)}%
                </div>
              </div>
            </Link>
          ))
        ) : (
          <div className="no-similar-jobs">
            <p>Không tìm thấy việc làm tương tự trong phòng ban <strong>{getDepartmentDisplayName(department)}</strong></p>
            <Link to={`/jobs?department=${department}`} className="view-dept-jobs">
              Xem tất cả việc làm {getDepartmentDisplayName(department)} →
            </Link>
            <Link to="/jobs" className="view-all-link">
              Xem tất cả việc làm →
            </Link>
          </div>
        )}
      </div>
      
      {similarJobs.length > 0 && (
        <div className="similar-jobs-footer">
          <Link to={`/jobs?department=${department}`} className="view-dept-jobs">
            Xem tất cả việc làm {getDepartmentDisplayName(department)} →
          </Link>
          <Link to="/jobs" className="view-all-similar">
            Xem tất cả việc làm →
          </Link>
        </div>
      )}
    </div>
  );
};

export default SimilarJobs; 